import { useEffect } from "react";
import { safeStorage } from "../../../../shared/lib/safeStorage";
import { PAYMENTS_ENABLED } from "../config/payments";

/**
 * HOW LONG AN ANONYMOUS GUEST IDENTITY LIVES ON THIS DEVICE (security audit
 * M1, 2026-09-27).
 *
 * The anonymous Firebase user is persisted in IndexedDB so a reload mid-flow
 * keeps its hold. Left alone it persisted FOREVER, and `ensureGuestIdentity`
 * reused whatever it found. On a shared device the next person to book, sign
 * in with Google or Apple (`linkWithPopup`) or create an account
 * (`linkWithCredential`) was handed the previous guest's UID, and with it that
 * guest's appointments and contact details.
 *
 * So the identity is retired (signed out, never deleted: the UID stays the
 * booking's `clientId` server-side) at two points, both in
 * `auth/guestRotation.ts`:
 *
 *  1. After a DEFINITE outcome (CONFIRMED or REQUESTED) has rendered. The
 *     guest's way back is the `/a/<token>` management link, a bearer
 *     capability that needs no session.
 *  2. After `GUEST_IDLE_LIMIT_MS` with nothing on this origin claiming it.
 *
 * NOTHING THAT NEEDS THE UID MAY LOSE IT. A hold is owned by the UID; a lost
 * answer is recovered by resending under the same UID's key; a guest code and
 * its proof are bound to the UID. Each of those registers a CLAIM while it is
 * live, bounded by the server instant its reason ends, and rotation refuses
 * while any claim exists, in this tab or (through Web Locks, where the browser
 * has them) in another tab of this origin, which shares the persisted identity.
 *
 * WHAT A REFUSED ROTATION BECOMES.
 *
 *  - After an outcome: DURABLE. The refusal is recorded as a pending
 *    retirement (`markRetirementPending`, no UID). The tab that booked waits
 *    for every claim to end (`whenAllUnclaimed`), and every tab reads the flag
 *    as an idle limit of zero, so the identity goes as soon as nothing claims
 *    it, even if the booking tab has closed.
 *  - For idleness: simply retried by the next idle check. A claim's end is
 *    activity; it restarts this tab's clock (`lastClaimEnded`, in memory) and
 *    the shared one (the activity timestamp in storage) that other tabs read.
 *    Without storage, another tab learns of it only through its own clock.
 *
 * This module holds no Firebase import on purpose: `lib/firebase.ts` reads the
 * rotation gate and records activity through it, and hooks that only claim
 * (useHold, useBooking) stay testable without Firebase.
 */

/*
 * THE ROTATION'S TWO PRODUCT PARAMETERS live here, together, and nowhere
 * else: `GUEST_IDLE_LIMIT_MS` (idle rotation) and `UNKNOWN_OUTCOME_CLAIM_MS`
 * (the unknown-outcome window). The idle limit is FIFTEEN minutes (Founder,
 * 2026-10-17; it was thirty). The unknown-outcome window is thirty minutes,
 * the Web Director's recommended default, still pending the Founder's
 * confirmation. guestSession.test.ts pins both values; changing either is a
 * Product decision, not a tuning.
 */

/**
 * FIFTEEN MINUTES WITHOUT ACTIVITY (Founder, 2026-10-17; it was thirty).
 *
 * Every UID-bound window Backend runs is shorter: a slot hold is 10 minutes
 * (and a re-hold resumes, never extends, it), a guest code 5 minutes with a
 * 60-second resend spacing, a verified proof 15 minutes. Anything live in the
 * page (a hold, a code, an unresolved attempt) is protected by a claim for as
 * long as its server window lasts, so this number only has to cover what
 * survives a RELOAD, and that is less than it looks (security audit F5): the
 * checkout's key, code and proof are held in memory, so they are gone after a
 * reload whatever the UID. What a kept UID buys across a reload is the
 * same-owner resume of a live hold, at most 10 minutes. Fifteen covers that.
 *
 * Shorter would collide with mail lag on the code step (about five minutes
 * observed) and slow form filling. Longer widens the window in which the next
 * person at a shared device inherits an abandoned identity; the Founder chose
 * the shorter lifetime.
 */
export const GUEST_IDLE_LIMIT_MS = 15 * 60 * 1000;

/** How often an open page re-checks idleness. Also re-checked when a tab returns. */
export const GUEST_IDLE_CHECK_MS = 60 * 1000;

/**
 * HOW LONG AN UNKNOWN OUTCOME KEEPS THE IDENTITY CLAIMED (security audit N1,
 * 2026-09-27): thirty minutes from entering it, renewed by each Check press.
 *
 * The Web Director's recommended default, AWAITING THE FOUNDER'S
 * CONFIRMATION. It narrows how long Check stays available: a
 * tab left on "we don't know yet" no longer holds the UID for as long as it
 * stays open, because another person at the same device could otherwise link
 * an account to it and inherit the booking it may have made. After the bound
 * the ordinary idle limit applies, and a Check pressed once the identity is
 * gone fails safe: the guest checkout withdraws it (`identity-lost` gives
 * `canCheck: false`), the legacy one sends nothing.
 */
export const UNKNOWN_OUTCOME_CLAIM_MS = 30 * 60 * 1000;

/** Slack over a documented server window, for clock skew and a slow answer. */
const CEILING_MARGIN_MS = 60 * 1000;

/**
 * THE LONGEST A CLAIM MAY TRUST A SERVER INSTANT (security audit N4). Each
 * claim that ends at a time the server sent is also capped at that window's
 * documented length plus a margin, measured from when the claim is taken, so
 * a wrong or hostile instant cannot hold the identity longer than Backend's
 * own contract allows. A hold acquisition in flight gets a short fixed bound.
 */
export const CLAIM_CEILING_MS = Object.freeze({
  hold: 10 * 60 * 1000 + CEILING_MARGIN_MS,
  code: 5 * 60 * 1000 + CEILING_MARGIN_MS,
  proof: 15 * 60 * 1000 + CEILING_MARGIN_MS,
  /**
   * TIED TO THE IDLE LIMIT ON PURPOSE (security audit N4: "limiter at most
   * the idle limit"; kept by the Web Director on the 2026-10-17 change to 15
   * minutes). The request-limited screen therefore holds the identity at
   * most this plus the idle limit (30 min). A lockout longer than this lets
   * the identity idle out, and the guest starts again from Details.
   */
  limiter: GUEST_IDLE_LIMIT_MS,
  acquiring: 60 * 1000,
  /**
   * A provider popup left open (security re-audit W1). Firebase keeps the
   * popup's promise pending for as long as the window stays open, so an
   * unbounded claim let an abandoned popup hold the identity indefinitely
   * while the next person booked under it. Ten minutes covers a real
   * provider sign-in; the link is re-checked after the popup either way
   * (auth/providerSignIn.ts).
   */
  popup: 10 * 60 * 1000 + CEILING_MARGIN_MS,
});

/** A timestamp only. Never a UID, a token, or anything about the booking. */
const ACTIVITY_KEY = "lura.book.guest-last-active";
/**
 * "A booked identity is waiting to be retired." A timestamp only, never the
 * UID: every tab of this origin shares one persisted identity, so the flag
 * means THE current anonymous identity, and it is cleared whenever that
 * identity goes (retired, replaced, or converted to an account).
 */
const RETIRE_KEY = "lura.book.guest-retire-pending";
/**
 * WHICH identity the pending retirement is for, without its UID (security
 * audit N7): that anonymous identity's creation instant, a timestamp only.
 * A tab waking from the background or the bfcache may have last seen a
 * different identity; only a current identity created at a different instant
 * proves the pending one is gone.
 */
const RETIRE_FOR_KEY = "lura.book.guest-retire-pending-created";
/** When this origin last saw a credentialed identity. A timestamp only. */
const CREDENTIALED_KEY = "lura.book.identity-credentialed-at";
const LOCK_NAME = "lura.book.guest-identity";

/**
 * HOW LONG ANOTHER TAB'S VIEW OF THE IDENTITY MAY LAG THIS ONE (security
 * audit F4). Firebase syncs its IndexedDB persistence across tabs by polling
 * (800 ms in the SDK). A tab that retires "the anonymous identity" inside this
 * window after any tab saw a credentialed one may be reading a stale user that
 * was linked moments ago, so it does not sign out; the pending flag and the
 * idle checks try again once every tab has caught up.
 */
export const CREDENTIALED_SYNC_WINDOW_MS = 10_000;

/**
 * A CLAIM'S END, KNOWN WHEN IT IS TAKEN (IF-1, root-caused 2026-10-13; security
 * audit of the plan, P-1 to P-4).
 *
 * A claim's release writes its end as activity and then lets go of its lock.
 * WebKit can grant another tab that lock while the other tab still reads
 * storage as it was BEFORE the write (2-10% of grants, measured; Chromium
 * never), and that tab then measured idleness from activity thirty minutes old
 * and retired the identity at the claim's end instead of a full idle limit
 * after it. So each tab also publishes, when it takes a finite claim, the
 * instant that claim ends: a stale view then shows either that instant or the
 * activity the release wrote, and both say "not idle".
 *
 * One key per tab (`lura.book.guest-claim-end.<random per-page id>`), because
 * storage has no atomic read-modify-write across tabs. Its value is
 * `<end>:<identity creation instant>`: timestamps only, never a UID, and only
 * the identity it was written for counts it (P-4). The id is made per page load
 * and kept in memory; it is also in the key's name while a key exists, and it
 * is never reused or sent anywhere.
 *
 * Bounded where it is written (P-1): a claim ending more than
 * `CLAIM_END_KEY_MAX_MS` after it is taken publishes nothing, so a key left
 * by a closed or crashed tab keeps the identity at most that long plus the
 * idle limit, the N1 maximum. Readers also ignore any value further ahead
 * than that, and remove keys older than the idle limit.
 */
export const CLAIM_END_KEY_MAX_MS = UNKNOWN_OUTCOME_CLAIM_MS;
const CLAIM_END_PREFIX = "lura.book.guest-claim-end.";
const CLAIM_END_KEY = /^lura\.book\.guest-claim-end\.[A-Za-z0-9_-]{16,36}$/;
const CLAIM_END_VALUE = /^(\d{1,16}):(\d{1,16})$/;

/**
 * "A HOSTED CHECKOUT IS IN FLIGHT FOR THIS IDENTITY" (payments v1.1.0;
 * docs/security/checkout-identity-claim-design.md, revision 3).
 *
 * Stripe-hosted Checkout navigates the page away, and `checkoutStatus(ref)`
 * answers only the uid that started it, so the anonymous identity must
 * survive the round trip. The page's claims, locks and claim-end key all go
 * when it unloads (P-2), so before `location.assign` the tab writes this
 * marker, which outlives the page by design.
 *
 *  - Key `lura.book.guest-checkout.<this page's random id>`, value
 *    `<until>:<identity creation instant>:<professional id>`: two timestamps
 *    and an id, never a uid, ref, URL, email or amount.
 *  - `until` = min(Checkout's expiry + 3 min, the write + 30 min).
 *  - While `now < until`, for the CURRENT anonymous identity, it is a CLAIM:
 *    the identity is not idle (§2.2), and a pending retirement waits for it
 *    inside a ceiling of 30 min from when the retirement was first recorded
 *    (§2.3). From `until` on it counts for NOTHING: no idle tail.
 *  - Void for any other identity, a malformed value, `until` already past or
 *    further ahead than 30 min. Removed by a reader for any of the time
 *    reasons, and for another identity only when the reader knows the current
 *    one (P-4). `pagehide` does NOT remove it; a retirement does.
 */
export const CHECKOUT_MARKER_SLACK_MS = 3 * 60 * 1000;
export const CHECKOUT_MARKER_MAX_MS = 30 * 60 * 1000;
/** How long a pending retirement may wait for live markers, from when it was first recorded (§2.3). */
export const RETIREMENT_DEFERRAL_CEILING_MS = 30 * 60 * 1000;
const CHECKOUT_PREFIX = "lura.book.guest-checkout.";
const CHECKOUT_KEY = /^lura\.book\.guest-checkout\.[A-Za-z0-9_-]{16,36}$/;
const CHECKOUT_VALUE = /^(\d{1,16}):(\d{1,16}):([A-Za-z0-9_-]{1,128})$/;
const PROFESSIONAL_ID = /^[A-Za-z0-9_-]{1,128}$/;

interface CheckoutMarker {
  readonly key: string;
  readonly until: number;
  readonly professionalId: string;
}

/**
 * A claim that lives past the page's use for it would hold the identity for
 * as long as the tab stays open (security audit F1). So each claim names the
 * instant its server-side reason ends (a hold's, a code's or a proof's expiry),
 * or `Infinity` only while a call is on the wire. An unknown outcome is
 * bounded too (`UNKNOWN_OUTCOME_CLAIM_MS`).
 */
interface Claim {
  readonly until: number;
  /** Its end is published to other tabs: finite, and within `CLAIM_END_KEY_MAX_MS` when taken. */
  readonly published: boolean;
  readonly release: () => void;
}

const claims = new Set<Claim>();
/**
 * THIS TAB'S OWN KNOWLEDGE OF A PENDING RETIREMENT (security audit N3), for
 * when storage refused the flag: set only if the write failed, so it cannot
 * outlive a flag another tab cleared. `waitingHere` counts this tab's
 * durable retirements still waiting for the lock.
 */
let pendingOnlyHere = false;
let waitingHere = 0;
let lastClaimEnded = Number.NEGATIVE_INFINITY;
let rotation: Promise<unknown> | null = null;
const listeners = new Set<() => void>();
let storageListening = false;
/**
 * THIS TAB'S CLAIM-END KEY. `tabId` is made once per page (undefined until
 * then, null where the browser has no random source: no key, today's
 * behaviour). `ownEnd` is what this tab last wrote and for which identity;
 * `clearedFor` is an identity whose key another tab removed while this one
 * held it (a retirement), which this tab then never writes again (P-3).
 * `guestIdentity` is the current anonymous identity's creation instant, as
 * the Firebase listener reports it (`noteGuestIdentity`).
 */
let tabId: string | null | undefined;
let ownEnd: { identity: number; end: number } | null = null;
let clearedFor: number | null = null;
let guestIdentity: number | null = null;
/**
 * The identity whose key this tab removed on pagehide, and whether activity
 * was recorded then (H-3). A retirement in another tab clears the activity
 * with every key, so activity that was there at pagehide and is gone at a
 * back/forward restore means retired: the restore does not publish for it.
 * Activity that was never there says nothing, and the restore publishes.
 */
let hidden: { identity: number; hadActivity: boolean } | null = null;
let pageListening = false;
/**
 * HOW MANY TIMES A RETIREMENT HAS BEEN RECORDED, here or in another tab
 * (security re-audit W1). Only ever increases: clearing the flag is not
 * "nothing happened", so a link that read the epoch before its popup can tell
 * that a retirement was recorded, and maybe cleared again, while it was open.
 */
let retireEpoch = 0;

/** setTimeout's ceiling; a longer delay would fire at once. */
const MAX_TIMER_MS = 2 ** 31 - 1;

function readTimestamp(key: string): number | null {
  const raw = safeStorage.local.get(key);
  if (raw === null || !/^\d{1,16}$/.test(raw)) return null;
  return Number(raw);
}

function writeTimestamp(key: string, now: number): boolean {
  return safeStorage.local.set(key, String(Math.floor(now)));
}

function removeKey(key: string): void {
  // Nothing to clear when storage is unavailable.
  safeStorage.local.remove(key);
}

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/** Another tab recorded or cleared a retirement. Installed once per page. */
function listenForOtherTabs(): void {
  if (storageListening || typeof window === "undefined") return;
  storageListening = true;
  window.addEventListener("storage", (event) => {
    if (event.key === RETIRE_KEY && event.newValue !== null) retireEpoch += 1;
    // A marker written or removed wakes a retirement waiting on it (§2.3);
    // correctness does not depend on it.
    if (event.key === RETIRE_KEY || event.key === null || (PAYMENTS_ENABLED && event.key.startsWith(CHECKOUT_PREFIX))) notify();
  });
}

/**
 * The retirement epoch (security re-audit W1). Read before a provider popup
 * opens and again after it: any change means a retirement was recorded while
 * the popup was open, so the identity it linked may own someone else's
 * booking. Reading it starts listening for other tabs, if nothing had yet.
 */
export function retirementEpoch(): number {
  listenForOtherTabs();
  return retireEpoch;
}

/**
 * Something changed that may make a rotation possible now: this tab's last
 * claim ended, or a retirement became pending here or in another tab (the
 * `storage` event). Returns the unsubscribe.
 */
export function onGuestSessionChange(listener: () => void): () => void {
  listeners.add(listener);
  listenForOtherTabs();
  return () => {
    listeners.delete(listener);
  };
}

/** The identity was just used for a hold, a booking, a check, or a code. */
export function recordGuestActivity(now: number = Date.now()): void {
  // Storage refused (quota, privacy mode): the sign-in time still bounds idleness.
  writeTimestamp(ACTIVITY_KEY, now);
}

/** On retirement: the activity and every tab's claim end go with the identity. */
export function clearGuestActivity(): void {
  removeKey(ACTIVITY_KEY);
  for (const key of safeStorage.local.keys(CLAIM_END_PREFIX)) removeKey(key);
  if (PAYMENTS_ENABLED) for (const key of safeStorage.local.keys(CHECKOUT_PREFIX)) removeKey(key);
  ownEnd = null;
}

/** The last recorded activity, or null. Anything but a plain integer is ignored. */
export function lastGuestActivity(): number | null {
  return readTimestamp(ACTIVITY_KEY);
}

/**
 * A DEFINITE OUTCOME'S RETIREMENT WAS REFUSED because something claimed the
 * identity (security audit F1). Persisted so every tab, and this tab after
 * the claim ends, retires the identity the moment nothing claims it: every tab
 * reads a pending retirement as an idle limit of zero.
 *
 * If storage refuses the write, the retirement is still retried by the tab
 * that booked, for as long as it stays open (auth/guestRotation.ts), and
 * otherwise falls back to the idle limit.
 */
export function markRetirementPending(now: number = Date.now(), identityCreatedAt: number | null = null): void {
  retireEpoch += 1;
  // THE CEILING'S ANCHOR IS THE FIRST RECORDING (checkout design §2.3, Fix 2):
  // a later refused outcome for the same identity must not restart the 30
  // minutes a checkout marker may defer the retirement. A future instant (a
  // clock moved back) is kept too: it allows no deferral at all.
  // Nor may a recording that does not know its identity move it later
  // (security code review F-4): only a KNOWN different identity's recording
  // replaces it. Keeping the older stamp only shortens any deferral.
  // The anchor bounds a checkout deferral only, so only with the payments gate
  // open; closed, every recording restamps, as before payments (rc.14).
  const pendingFor = PAYMENTS_ENABLED ? readTimestamp(RETIRE_FOR_KEY) : null;
  const anotherIdentity = identityCreatedAt !== null && pendingFor !== null && pendingFor !== identityCreatedAt;
  if (PAYMENTS_ENABLED && readTimestamp(RETIRE_KEY) !== null && !anotherIdentity) {
    // Name the identity when the recording did not (N7 can then clear it by
    // a mismatch), without moving the stamp (security code review, F-4 note).
    if (pendingFor === null && identityCreatedAt !== null) writeTimestamp(RETIRE_FOR_KEY, identityCreatedAt);
    pendingOnlyHere = false;
    notify();
    return;
  }
  pendingOnlyHere = !writeTimestamp(RETIRE_KEY, now);
  if (identityCreatedAt === null || !writeTimestamp(RETIRE_FOR_KEY, identityCreatedAt)) removeKey(RETIRE_FOR_KEY);
  notify();
}

/** The identity it was about is gone, replaced, or now an account. */
export function clearRetirementPending(): void {
  pendingOnlyHere = false;
  removeKey(RETIRE_KEY);
  removeKey(RETIRE_FOR_KEY);
}

/** The creation instant of the identity a retirement is pending for, or null when unknown. */
export function pendingRetirementIdentity(): number | null {
  return readTimestamp(RETIRE_FOR_KEY);
}

/** Recorded, in storage or (storage refused) in this tab: an idle limit of zero. */
function retirementRecorded(): boolean {
  return readTimestamp(RETIRE_KEY) !== null || pendingOnlyHere;
}

/**
 * Is a retirement pending? The question account creation and provider
 * sign-in ask before linking (never link a pending identity). Also true while
 * this tab is itself waiting to retire one, whatever storage managed to keep.
 */
export function isRetirementPending(): boolean {
  return retirementRecorded() || waitingHere > 0;
}

/** auth/guestRotation.ts: a durable retirement in this tab started (+1) or ended (-1). */
export function noteRetirementWaiting(delta: 1 | -1): void {
  waitingHere = Math.max(0, waitingHere + delta);
}

/** A credentialed identity is current on this origin (IdentityProvider). */
export function recordCredentialedIdentity(now: number = Date.now()): void {
  writeTimestamp(CREDENTIALED_KEY, now);
}

/** Did any tab see a credentialed identity within the sync window? (F4) */
export function credentialedRecently(now: number = Date.now()): boolean {
  const at = readTimestamp(CREDENTIALED_KEY);
  return at !== null && Math.abs(now - at) < CREDENTIALED_SYNC_WINDOW_MS;
}

/**
 * Has this identity been idle for the limit?
 *
 * `signedInAt` is the anonymous sign-in instant (Firebase `lastSignInTime`),
 * which covers an identity created before activity was recorded and a device
 * whose storage refused the write. An identity with no known activity at all
 * is idle: rotating one with nothing claiming it costs nothing. A pending
 * retirement makes the limit zero, whatever any claim's end says: checked
 * first, so a published end never holds back a retirement.
 *
 * `identityCreatedAt` (the identity's creation instant) selects the claim
 * ends other tabs published for THIS identity (IF-1); unknown, none count.
 */
export function isGuestIdle(
  signedInAt: number | null,
  now: number = Date.now(),
  identityCreatedAt: number | null = null
): boolean {
  // Checked first: a published end never holds back a retirement. Only a
  // live checkout marker does, inside the ceiling (checkout design §2.3).
  // With the payments gate closed none of the checkout terms ship (rc.14).
  if (retirementRecorded()) return !PAYMENTS_ENABLED || retirementDeferredUntil(now, identityCreatedAt, null) === null;
  // A checkout in flight is a claim until its `until`, never activity (§2.2).
  if (PAYMENTS_ENABLED && checkoutMarkers(identityCreatedAt, now).length > 0) return false;
  const last = Math.max(
    lastGuestActivity() ?? Number.NEGATIVE_INFINITY,
    signedInAt ?? Number.NEGATIVE_INFINITY,
    lastClaimEnded,
    ...publishedClaimEnds(identityCreatedAt, now)
  );
  return now - last >= GUEST_IDLE_LIMIT_MS;
}

/**
 * The claim ends every tab published for this identity. A key or value of any
 * other shape is ignored, as is an end further ahead than any claim may
 * publish (a hostile value, or a clock moved back). A key older than the idle
 * limit can no longer keep anything and is removed, whoever wrote it.
 */
function publishedClaimEnds(identityCreatedAt: number | null, now: number): number[] {
  const ends: number[] = [];
  for (const key of safeStorage.local.keys(CLAIM_END_PREFIX)) {
    if (!CLAIM_END_KEY.test(key)) continue;
    const match = CLAIM_END_VALUE.exec(safeStorage.local.get(key) ?? "");
    if (!match) continue;
    const end = Number(match[1]);
    if (now - end > GUEST_IDLE_LIMIT_MS) {
      removeKey(key);
      continue;
    }
    if (identityCreatedAt === null || Number(match[2]) !== identityCreatedAt) continue;
    if (end > now + CLAIM_END_KEY_MAX_MS) continue;
    ends.push(end);
  }
  return ends;
}

/** This page's random id (its claim-end and checkout keys), or null where there is no random source. */
function pageTabId(): string | null {
  if (tabId === undefined) {
    try {
      const random = globalThis.crypto;
      if (typeof random?.randomUUID === "function") tabId = random.randomUUID();
      else if (typeof random?.getRandomValues === "function") {
        tabId = Array.from(random.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
      } else tabId = null;
    } catch {
      tabId = null;
    }
  }
  return tabId;
}

function claimEndKey(): string | null {
  const id = pageTabId();
  return id === null ? null : `${CLAIM_END_PREFIX}${id}`;
}

/**
 * Bring this tab's claim-end key up to date (IF-1; P-3).
 *
 *  - A live published claim: the key holds the latest of their ends.
 *  - None left, after a release: the key is LOWERED to `ended`, never removed
 *    here, so every view another tab may have, old or new, shows an end at
 *    least as late as the activity the release wrote. Readers remove it once
 *    it is older than the idle limit.
 *  - A key this tab wrote that is gone while it could still count was removed
 *    by a retirement in another tab: this tab never writes one for that
 *    identity again.
 *  - Nothing is written for an unknown identity, and storage that refuses
 *    the write leaves today's behaviour.
 */
function syncClaimEnd(now: number, ended: number = Number.NEGATIVE_INFINITY): void {
  const key = claimEndKey();
  if (key === null) return;
  noticeOwnKeyGone(now);
  const identity = guestIdentity;
  if (identity === null || identity === clearedFor) return;
  let end = Number.NEGATIVE_INFINITY;
  for (const claim of claims) if (claim.published && claim.until > now) end = Math.max(end, claim.until);
  if (end === Number.NEGATIVE_INFINITY) {
    // Nothing live: only a release lowers a key this tab already has.
    if (ownEnd === null || ownEnd.identity !== identity || !Number.isFinite(ended)) return;
    // Never below an end this tab already released: a batch release can end
    // the later claim first (reviewer P3, 2026-10-13).
    end = Math.max(ended, lastClaimEnded);
  }
  end = Math.floor(end);
  if (ownEnd?.identity === identity && ownEnd.end === end) return;
  if (safeStorage.local.set(key, `${end}:${identity}`)) ownEnd = { identity, end };
}

/**
 * Is this tab's key gone? A retirement clears the activity with every key; a
 * reader's collection on a stale view (or a sweep on a stale identity view)
 * removes only the key (reviewer P3, 2026-10-13). So a missing key counts as
 * a retirement (`clearedFor`) only when the activity went too; otherwise the
 * next sync writes it again, even a release that leaves no live claim. A
 * release runs this BEFORE it records its own activity.
 */
function noticeOwnKeyGone(now: number): void {
  const key = claimEndKey();
  if (key === null || ownEnd === null) return;
  if (CLAIM_END_VALUE.test(safeStorage.local.get(key) ?? "")) return;
  if (now - ownEnd.end > GUEST_IDLE_LIMIT_MS) {
    ownEnd = null;
    return;
  }
  if (lastGuestActivity() === null) {
    clearedFor = ownEnd.identity;
    ownEnd = null;
    return;
  }
  // Collected, not retired: keep the identity but forget the value, so the
  // sync that follows writes it again, a release's lowered end included
  // (reviewer, 2026-10-14).
  ownEnd = { identity: ownEnd.identity, end: Number.NaN };
}

/**
 * The page is going away (P-2): an ordinary close must not leave this tab's
 * end behind, or "close the tab and walk away" would keep the identity a full
 * claim window plus the idle limit on a shared device. Restored from the
 * back/forward cache, the claims still live publish again.
 */
function listenForPageLifecycle(): void {
  if (pageListening || typeof window === "undefined") return;
  pageListening = true;
  window.addEventListener("pagehide", () => {
    const key = claimEndKey();
    if (key !== null && ownEnd !== null) {
      removeKey(key);
      hidden = { identity: ownEnd.identity, hadActivity: lastGuestActivity() !== null };
    }
    ownEnd = null;
  });
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    // Retired in another tab while this page was away (its activity cleared
    // with every key): publishing again would only re-create a key that
    // counts for nothing (security audit H-3; P-4).
    if (hidden?.hadActivity && lastGuestActivity() === null) clearedFor = hidden.identity;
    hidden = null;
    const now = Date.now();
    releaseExpired(now);
    syncClaimEnd(now);
  });
}

/**
 * LEFTOVER KEYS GO (security audit H-2). Otherwise only a guest's idle check
 * collects old keys, so under a signed-in account a killed tab's key, a
 * malformed one or a far-ahead one would stay. On every identity note (the
 * Firebase listener's first call is this page's load), remove every claim-end
 * key that is malformed, older than the idle limit, further ahead than any
 * claim may publish, or for an identity other than the KNOWN current one.
 * With no known identity (signed in, signed out, or an anonymous one whose
 * creation instant Firebase does not give) nothing is removed for its
 * identity alone: another tab may know it.
 */
function sweepClaimEnds(identity: number | null, now: number): void {
  for (const key of safeStorage.local.keys(CLAIM_END_PREFIX)) {
    const match = CLAIM_END_KEY.test(key) ? CLAIM_END_VALUE.exec(safeStorage.local.get(key) ?? "") : null;
    const end = match ? Number(match[1]) : Number.NaN;
    const stale =
      !match ||
      now - end > GUEST_IDLE_LIMIT_MS ||
      end > now + CLAIM_END_KEY_MAX_MS ||
      (identity !== null && Number(match[2]) !== identity);
    if (stale) removeKey(key);
  }
}

/**
 * The current anonymous identity's creation instant, or null (none, a
 * credentialed one, or Firebase does not say). From the Firebase listener
 * (auth/guestRotation.ts `noteIdentityChange`). A new identity publishes this
 * tab's live claims under it; no anonymous identity takes this tab's key away.
 */
export function noteGuestIdentity(createdAt: number | null): void {
  sweepClaimEnds(createdAt, Date.now());
  // The same for checkout markers (§2.4): the sweep removes the void ones.
  checkoutMarkers(createdAt, Date.now(), true);
  if (createdAt === guestIdentity) return;
  guestIdentity = createdAt;
  if (createdAt === null) {
    const key = claimEndKey();
    if (key !== null && ownEnd !== null) removeKey(key);
    ownEnd = null;
    return;
  }
  syncClaimEnd(Date.now());
}

/**
 * The live checkout markers for `identity` (none for an unknown one). Reading
 * removes every marker that is malformed, past its `until` or further ahead
 * than any write may set. Another identity's marker is only IGNORED here; the
 * identity sweep (`sweep`, from `noteGuestIdentity`) removes it, because a
 * hot reader's identity may be stale (a thawed tab, N7) and must not delete
 * the current identity's live marker (security code review F-3; the
 * claim-end pattern of `publishedClaimEnds` and `sweepClaimEnds`).
 */
function checkoutMarkers(identity: number | null, now: number, sweep = false): CheckoutMarker[] {
  // The payments gate (config/payments.ts): nothing writes a marker while it
  // is off, and none of this ships.
  if (!PAYMENTS_ENABLED) return [];
  const live: CheckoutMarker[] = [];
  for (const key of safeStorage.local.keys(CHECKOUT_PREFIX)) {
    const match = CHECKOUT_KEY.test(key) ? CHECKOUT_VALUE.exec(safeStorage.local.get(key) ?? "") : null;
    const until = match ? Number(match[1]) : Number.NaN;
    if (!match || until <= now || until > now + CHECKOUT_MARKER_MAX_MS) {
      removeKey(key);
      continue;
    }
    if (identity === null) continue;
    if (Number(match[2]) !== identity) {
      if (sweep) removeKey(key);
      continue;
    }
    live.push({ key, until, professionalId: match[3]! });
  }
  return live;
}

/**
 * WRITE THE MARKER, just before leaving for Checkout (design §2.1): only from
 * a user's press, after the URL guard accepted (`leaveForCheckout`'s
 * `beforeLeave`), never on a load or `pageshow`. Writes nothing, and returns
 * false (today's behaviour), when the current identity is not a known
 * anonymous one, Checkout's expiry is not a future instant, the professional
 * id is malformed, the page has no random id, or storage refuses.
 */
export function writeCheckoutMarker(checkoutExpiresAt: number, professionalId: string, now: number = Date.now()): boolean {
  if (!PAYMENTS_ENABLED) return false;
  const identity = guestIdentity;
  if (identity === null) return false;
  if (!Number.isFinite(checkoutExpiresAt) || checkoutExpiresAt <= now) return false;
  if (!PROFESSIONAL_ID.test(professionalId)) return false;
  const id = pageTabId();
  if (id === null) return false;
  const until = Math.floor(Math.min(checkoutExpiresAt + CHECKOUT_MARKER_SLACK_MS, now + CHECKOUT_MARKER_MAX_MS));
  return safeStorage.local.set(`${CHECKOUT_PREFIX}${id}`, `${until}:${identity}:${professionalId}`);
}

/**
 * Is a checkout in flight for the current anonymous identity? With
 * `professionalId`, only one for that professional counts (the Web
 * Director's Q-A: Review blocks a new attempt only for the same one).
 * Without, any: a link or provider sign-in is refused (H1).
 */
export function checkoutInFlight(
  professionalId?: string,
  now: number = Date.now(),
  identity: number | null = guestIdentity
): boolean {
  return checkoutMarkers(identity, now).some((m) => professionalId === undefined || m.professionalId === professionalId);
}

/**
 * An anonymous user's creation instant (whole ms), or null when Firebase does
 * not say. For callers holding the user itself (H1 reads the guest it would
 * link, not this module's last-noted identity: security code review F-5).
 */
export function identityCreatedAt(user: { readonly metadata?: { readonly creationTime?: string } } | null | undefined): number | null {
  const at = user?.metadata?.creationTime ? Date.parse(user.metadata.creationTime) : Number.NaN;
  return Number.isFinite(at) ? Math.floor(at) : null;
}

/**
 * THE TERMINAL CLEAR (§2.4): every marker for the current identity goes, not
 * only this page's (the return page has a new id and cannot name the
 * writer's key). A deliberate cross-tab delete that fails toward retirement.
 * Nothing is removed for an unknown identity.
 */
export function clearCheckoutMarkers(now: number = Date.now()): void {
  const identity = guestIdentity;
  if (identity === null) return;
  for (const marker of checkoutMarkers(identity, now)) removeKey(marker.key);
  notify();
}

/**
 * WHEN A PENDING RETIREMENT MAY RUN, if a live marker defers it (§2.3): the
 * earliest live `until`, capped at the ceiling of 30 min from when the
 * retirement was FIRST recorded. Null means no deferral: no live marker, no
 * readable recording (storage refused it: `pendingOnlyHere`), a recording in
 * the future, or the ceiling passed. `unrecordedAt` is the instant to anchor
 * on when nothing is recorded yet (a durable rotation's first attempt, which
 * records the retirement right after it is refused).
 */
function retirementDeferredUntil(now: number, identity: number | null, unrecordedAt: number | null): number | null {
  const markers = checkoutMarkers(identity, now);
  if (markers.length === 0 || pendingOnlyHere) return null;
  const recorded = readTimestamp(RETIRE_KEY) ?? unrecordedAt;
  if (recorded === null || recorded > now) return null;
  const ceiling = recorded + RETIREMENT_DEFERRAL_CEILING_MS;
  if (now >= ceiling) return null;
  return Math.min(ceiling, ...markers.map((m) => m.until));
}

/** The current anonymous identity's creation instant, as noted (`noteGuestIdentity`), or null. */
export function currentGuestIdentity(): number | null {
  return guestIdentity;
}

/** The return page's minimum claim: long enough for its first reads (§2.5). */
export const RETURN_READ_MIN_MS = 60 * 1000;

/**
 * HOW LONG THE CHECKOUT RETURN PAGE MAY CLAIM THE IDENTITY (§2.5, Fix 3):
 * until the matching marker's `until`, never past the §2.3 ceiling while a
 * retirement is pending, and at least `RETURN_READ_MIN_MS` for the read. At
 * module evaluation the identity is not known yet (`identity` null): the
 * latest well-formed marker counts, already capped at 30 minutes from its
 * write. Reading here removes nothing. `now` itself (no claim) when a
 * recorded retirement may not be deferred. Take it QUIET
 * (`claimGuestIdentity(until, max, { quiet: true })`): it must publish no end
 * and record no activity.
 */
export function checkoutReturnClaimUntil(identity: number | null, now: number = Date.now()): number {
  let until = Number.NEGATIVE_INFINITY;
  if (PAYMENTS_ENABLED) for (const key of safeStorage.local.keys(CHECKOUT_PREFIX)) {
    const match = CHECKOUT_KEY.test(key) ? CHECKOUT_VALUE.exec(safeStorage.local.get(key) ?? "") : null;
    if (!match) continue;
    const end = Number(match[1]);
    if (end <= now || end > now + CHECKOUT_MARKER_MAX_MS) continue;
    if (identity !== null && Number(match[2]) !== identity) continue;
    until = Math.max(until, end);
  }
  if (retirementRecorded()) {
    const recorded = readTimestamp(RETIRE_KEY);
    // No readable or a future recording, or the ceiling passed, allows no
    // deferral (§2.3): the retirement wins, and the page takes no claim
    // (security code review of increment 3, item 3).
    if (pendingOnlyHere || recorded === null || recorded > now) return now;
    const ceiling = recorded + RETIREMENT_DEFERRAL_CEILING_MS;
    if (now >= ceiling) return now;
    // Only a marker may defer a retirement (Fix 2): with none that counts,
    // no claim (security code review R-4).
    if (until === Number.NEGATIVE_INFINITY) return now;
    return Math.min(ceiling, Math.max(until, now + RETURN_READ_MIN_MS));
  }
  return Math.max(until, now + RETURN_READ_MIN_MS);
}

/** A durable rotation's first attempt: does a live marker for `identity` defer it? (§2.3) */
export function retirementDeferredByCheckout(identity: number | null, now: number = Date.now()): boolean {
  return retirementDeferredUntil(now, identity, now) !== null;
}

/*
 * WITHOUT WEB LOCKS (Safari before 15.4, and any browser that withholds
 * `navigator.locks`), claims are PER TAB. A rotation in one tab cannot see
 * another tab's claim and goes ahead: it fails closed for M1, retiring the
 * identity, and the other tab's hold, code or unknown outcome then meets a
 * different (or no) identity and says so (`identity-lost`, a withdrawn Check)
 * rather than acting under the wrong one. The ends other tabs' finite claims
 * published (IF-1) are still read there: the idle rotation keeps the
 * identity until a full idle limit after them, as with locks, while the
 * retirement after an outcome never reads idleness and is unaffected.
 */
function lockManager(): LockManager | null {
  if (typeof navigator === "undefined" || !("locks" in navigator)) return null;
  return navigator.locks ?? null;
}

/** Release every claim whose server-side reason has ended (a throttled timer's backstop). */
function releaseExpired(now: number): boolean {
  let released = false;
  for (const claim of [...claims]) {
    if (claim.until <= now) {
      claim.release();
      released = true;
    }
  }
  return released;
}

/**
 * Something live needs the current UID until `until` (a server instant, in
 * epoch milliseconds; `Infinity` while a call is on the wire). Returns the
 * release, which is idempotent and also runs by itself at `until`. Releasing
 * restarts the idle clock: a hold that just ended is activity, not idleness.
 * A claim whose instant has already passed is not taken at all. `maxMs` caps
 * it, from now, at the reason's documented ceiling (`CLAIM_CEILING_MS`); an
 * unreadable instant (NaN) is no claim.
 */
export function claimGuestIdentity(
  requestedUntil: number = Number.POSITIVE_INFINITY,
  maxMs: number = Number.POSITIVE_INFINITY,
  /**
   * QUIET (the checkout return page, security code review of increment 3):
   * the claim keeps the identity while it lives, but publishes no end and
   * records NO activity when it ends. A checkout marker is a claim until its
   * `until`, never activity (§2.2, R2); a loud claim held to `until` would
   * make the identity live until `until` + the idle limit.
   */
  options?: { readonly quiet?: boolean }
): () => void {
  // Only the checkout return page asks for a quiet claim (payments gate).
  const quiet = PAYMENTS_ENABLED && options?.quiet === true;
  const until = Math.min(requestedUntil, Date.now() + maxMs);
  if (!(until > Date.now())) return () => undefined;
  let settle: () => void = () => undefined;
  const held = new Promise<void>((resolve) => {
    settle = resolve;
  });
  // Shared across tabs: another tab's rotation asks for this lock exclusively,
  // so it never breaks a claim; a durable retirement waits for it to end.
  void lockManager()
    ?.request(LOCK_NAME, { mode: "shared" }, () => held)
    .catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const claim: Claim = {
    until,
    published: !quiet && Number.isFinite(until) && until <= Date.now() + CLAIM_END_KEY_MAX_MS,
    release: () => {
      if (!claims.delete(claim)) return;
      if (timer !== undefined) clearTimeout(timer);
      if (quiet) {
        settle();
        if (claims.size === 0) notify();
        return;
      }
      const now = Date.now();
      const ended = Math.min(now, until);
      noticeOwnKeyGone(now);
      lastClaimEnded = Math.max(lastClaimEnded, ended);
      // Shared with the other tabs, whose own `lastClaimEnded` never sees it.
      if (ended > (lastGuestActivity() ?? Number.NEGATIVE_INFINITY)) recordGuestActivity(ended);
      // Lowered, never removed (P-3): no view another tab may have shows less.
      syncClaimEnd(now, ended);
      settle();
      if (claims.size === 0) notify();
    },
  };
  claims.add(claim);
  if (claim.published) {
    listenForPageLifecycle();
    syncClaimEnd(Date.now());
  }
  const delay = until - Date.now();
  if (Number.isFinite(until) && delay <= MAX_TIMER_MS) timer = setTimeout(claim.release, delay);
  return claim.release;
}

export function hasLocalGuestClaims(now: number = Date.now()): boolean {
  releaseExpired(now);
  return claims.size > 0;
}

/**
 * Hold a claim while `active`, and at most until `until` (capped at `maxMs`
 * from when it is taken). The effect's cleanup is the release; a new `until`
 * replaces the claim.
 */
export function useGuestIdentityClaim(
  active: boolean,
  until: number = Number.POSITIVE_INFINITY,
  maxMs: number = Number.POSITIVE_INFINITY
): void {
  useEffect(() => {
    if (!active) return;
    return claimGuestIdentity(until, maxMs);
  }, [active, until, maxMs]);
}

/** A task's answer, or the claim that kept it from running. */
export type GateResult = { ran: true; result: boolean } | { ran: false };

/**
 * Run `task` only if NOTHING on this origin claims the identity, deciding
 * without waiting: a claim anywhere means "not now", never "after".
 */
export async function gateUnclaimed(task: () => Promise<boolean>): Promise<GateResult> {
  if (releaseExpired(Date.now())) await yieldToLocks();
  if (claims.size > 0) return { ran: false };
  const locks = lockManager();
  if (!locks) return { ran: true, result: await task() };
  return locks.request(LOCK_NAME, { mode: "exclusive", ifAvailable: true }, async (lock): Promise<GateResult> => {
    if (!lock) return { ran: false };
    // Decided in a later task than the grant (P-5): WebKit can grant this
    // lock while this tab still reads storage as it was before the holder's
    // release wrote to it, and that view lasts until the next task. Not a
    // guarantee of ordering, only of a fresher view for every read the task
    // makes (activity, the credentialed stamp, a pending retirement).
    await yieldToLocks();
    if (claims.size > 0) return { ran: false };
    return { ran: true, result: await task() };
  });
}

export async function whenUnclaimed(task: () => Promise<boolean>): Promise<boolean> {
  const gate = await gateUnclaimed(task);
  return gate.ran && gate.result;
}

/** Asked again at once: the claim that got in first is now queued ahead of the next request. */
const AGAIN = Symbol("again");
/** Asked again after this tab's own claims change: a claim without a lock cannot be queued behind. */
const AFTER_LOCAL = Symbol("after-local");

/** How long a waiting retirement sleeps when this tab's own claim is the obstacle. */
const LOCAL_CLAIM_RETRY_MS = 1_000;

/**
 * Run `task` once NOTHING on this origin claims the identity, WAITING for that
 * (security audit F1): an exclusive Web Lock request that is not `ifAvailable`
 * is granted when every tab's shared claim has ended. Inside it, a claim this
 * tab or another took while the request was queued (a shared request now
 * queued behind this one) sends it back to wait again, so a claim is never
 * overtaken. Stops, with false, as soon as `wanted()` says the retirement is
 * no longer this identity's.
 */
export async function whenAllUnclaimed(task: () => Promise<boolean>, wanted: () => boolean): Promise<boolean> {
  for (;;) {
    if (!wanted()) return false;
    const locks = lockManager();
    const outcome: boolean | typeof AGAIN | typeof AFTER_LOCAL | Deferred = locks
      ? await locks.request(LOCK_NAME, { mode: "exclusive" }, async () => {
          // FIRST (checkout design §2.3, Fix 1; placed first by the security
          // code review, F-1): WebKit can grant this lock on the writer's
          // unload while this tab still reads storage from before the marker
          // was written (the IF-1 stale view). One task later every check
          // below reads fresh, and a claim taken during the yield is seen, so
          // it is never overtaken. NOT gated (security audit of the rc.14
          // fold, W-1): the same stale view lets the durable retirement's
          // retireIfStill miss a just-written credentialed stamp (the Q2 race,
          // docs/security/book-guest-identity-storage.md), which rc.13 had
          // with no yield here. Gate off, this narrows that race.
          await yieldToLocks();
          if (!wanted()) return false;
          if (await sharedClaimsQueued(locks)) return AGAIN;
          if (hasLocalGuestClaims()) return AFTER_LOCAL;
          return PAYMENTS_ENABLED ? deferredOr(task) : task();
        })
      : hasLocalGuestClaims()
        ? AFTER_LOCAL
        : await (PAYMENTS_ENABLED ? deferredOr(task) : task());
    if (outcome === AFTER_LOCAL) await nextChangeOr(LOCAL_CLAIM_RETRY_MS);
    // A checkout in flight: sleep until its `until` or the ceiling, or a
    // change (a marker written or removed), then ask again. Never "false".
    else if (typeof outcome === "object") {
      // Unreachable with the gate closed (nothing defers); never spin there.
      if (!PAYMENTS_ENABLED) return false;
      await nextChangeOr(Math.max(0, outcome.until - Date.now()));
    }
    else if (outcome !== AGAIN) return outcome;
  }
}

/** A pending retirement a checkout marker defers, and until when. */
type Deferred = { readonly until: number };

function deferredOr(task: () => Promise<boolean>): Promise<boolean> | Deferred {
  const until = PAYMENTS_ENABLED ? retirementDeferredUntil(Date.now(), guestIdentity, null) : null;
  return until === null ? task() : { until };
}

async function sharedClaimsQueued(locks: LockManager): Promise<boolean> {
  try {
    const snapshot = await locks.query();
    return (snapshot.pending ?? []).some((lock) => lock.name === LOCK_NAME && lock.mode === "shared");
  } catch {
    return false;
  }
}

function nextChangeOr(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = setTimeout(done, ms);
    const unsubscribe = onGuestSessionChange(done);
  });
}

/**
 * One task later: lets a just-settled shared lock actually be released before
 * asking for the exclusive one, and lets storage written before a lock's
 * release reach this tab before it decides inside the lock.
 */
function yieldToLocks(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * THE GATE `ensureGuestIdentity` WAITS ON. Set synchronously when a rotation
 * starts, so an identity read that begins while one is under way gets the NEW
 * identity rather than the one being retired.
 */
export function trackGuestRotation<T>(run: Promise<T>): Promise<T> {
  rotation = run;
  void run.finally(() => {
    if (rotation === run) rotation = null;
  }).catch(() => undefined);
  return run;
}

export async function guestRotationSettled(): Promise<void> {
  const pending = rotation;
  if (pending) await pending.catch(() => undefined);
}

/** Test-only. */
export function __resetGuestSessionForTests(): void {
  for (const claim of [...claims]) claim.release();
  claims.clear();
  lastClaimEnded = Number.NEGATIVE_INFINITY;
  rotation = null;
  listeners.clear();
  pendingOnlyHere = false;
  waitingHere = 0;
  ownEnd = null;
  clearedFor = null;
  hidden = null;
  guestIdentity = null;
  clearGuestActivity();
  clearRetirementPending();
  removeKey(CREDENTIALED_KEY);
}
